import { HoursOfOperation, ITimeZone } from './../../shared/models/settings.model';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, UntypedFormControl, UntypedFormArray } from '@angular/forms';
import { AlertService } from './../../shared/services/alert.service';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { Departments } from '../../shared/enums/departments.enum';
import { IDuration, IHoursOfOperation, Settings } from '../../shared/models/settings.model';
import { SettingsService } from '../../shared/services/settings.service';
import * as fromActions from '../../store/settings/settings.actions';
import { SettingsState } from '../../store/settings/settings.reducer';
import * as fromSelector from '../../store/settings/settings.selectors';
import { TransportationType } from '@signal/asp-data-commons';
import { AvailabilityService } from '../../shared/services/availability.service';
import { DateTime } from 'luxon';
import { TranslateService } from '@ngx-translate/core';
import * as util from 'src/app/modules/shared/services/util.service';
import { SlotState } from '../../store/slot/slot.reducer';
import * as fromSlotSelector from '../../store/slot/slot.selectors';
import * as fromSlotActions from '../../store/slot/slot.actions';
import { ShopMaxPerDay } from '../../shared/models/slot-management.model';
import { SubscriptionList } from '../../shared/models/asp.types';
import { INavigateAway } from '../../shared/guards/NavigationGuard';
import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { exitConfig, environment } from 'src/app/modules/shared/services/util.service';

@Component({
  selector: 'app-service-settings',
  templateUrl: './service-settings.component.html',
  styleUrls: ['./service-settings.component.scss']
})
export class ServiceSettingsComponent implements OnInit ,INavigateAway, OnDestroy{
  department: string = Departments.SERVICE;
  //transportCheckd : string;
  settingsForm: UntypedFormGroup;
  settings$: Observable<Settings>;
  zipcodes$: Observable<string[]>;
  zipcodes: string[] = [];
  scheduleValues: string[] = ["1 Day", "2 Days", "3 Days", "5 Days", "7 Days", "14 Days", "21 Days"];
  amenities: string[] = [];
  hasAmenities = false;
  hoursOfOperation: HoursOfOperation[] = [];
  timeDuration: IDuration[];
  curOffTime: IDuration[];
  subs: SubscriptionList = {};
  isUsDealer: boolean = util.countryCd.toUpperCase().includes('US');
  syncInProgress=false;
  timezones$: Observable<ITimeZone[]>;
  notificationList = [
    { name: 'syncFailure', header: 'generalSettings.failedNotifications' },
    { name: 'appointment', header: 'generalSettings.appointmentNotifications' },
    { name: 'sscRecall', header: 'generalSettings.ssc/RecallNotifications' },
    { name: 'telematics', header: 'generalSettings.telematicsNotifications' },
    { name: 'rental', header: 'generalSettings.rentalNotifications' }
  ];
  notification = {
    syncFailure: true,
    rental: false,
    sscRecall: false,
    telematics: false,
    appointment: false
  };
  portalSwitches = {
    showPricing: false,
    showEstimatedTime: true,
    mandateVin: false,
    mandateCustomerAddress: false,
    enableAmenities: true,
  };
  alexaEnabled: boolean = true;
  /* made false everywhere - needs change when enabled */
  customerNotificationEnabled: boolean = false;
  reminderNotificationEnabled: boolean = false;
  dealerLexusFlag: boolean = false;
  tooltip: any;
  servicePersonalSetup: number = 0;
  capacityToggleItems: Array<string>;
  isDisUS: boolean = true;
  generalsettingInfo: string;
  detailedCapcitySetup: number = 0;
  shopMaxTotal$: Observable<ShopMaxPerDay[]>;
  weekdays = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  isEnglishText: boolean = true;
  saveInProgress = false;

  constructor(private readonly settingsState: Store<SettingsState>, private readonly fb: UntypedFormBuilder,
    private readonly settingsService: SettingsService, private readonly alertService: AlertService, 
    private readonly availabiltyService: AvailabilityService, private readonly translate: TranslateService,
    private readonly slotState: Store<SlotState>,
    private readonly dialog: MatDialog) {
    this.setUpStore();
    this.initializeSubscriptions();
    this.initiateForm();
    this.tooltip = {
      time: this.translate.instant('generalSettings.toUpdateTheOperationalHoursGoToTheToyotaDISsystem'),
      transport: this.translate.instant('generalSettings.setTheTransportOptionsOfferedByYourDealership'),
      customerNotification: this.translate.instant('generalSettings.enableToAllowUsersToReviceEmailNotifications'),
      reminderNotification: this.translate.instant('generalSettings.enableToAllowUsersToReviceReminderNotifications'),
      alexa: this.translate.instant('generalSettings.enableToAllowUsersToBookAppointmentsThroughAlexa'),
      portal: this.translate.instant('generalSettings.manageSettingsOntheCustomerOnlinePortal'),
      notifications: this.translate.instant('generalSettings.automatedEmailsWillBeSentForTheTriggerPointsYouSetHere'),
      capacity: this.translate.instant('generalSettings.toUpdateTheASPFulfilmentSlabsAndGaragesDailyFlatRateHourCapacityCalculationMethod')
    };
    this.generalsettingInfo = this.translate.instant(`generalSettings.${this.translate.instant('COUNTRYTRANSLATION.gsUpdateInfo')}`);
    this.isDisUS = (util.countryCd === 'USA') ? true : false;
    if (this.translate.currentLang?.includes('es')) {
      this.isEnglishText = false;
    }
  }

  ngOnInit(): void {
    const startDate = DateTime.now().toFormat("yyyy-MM-dd");
    this.subs.availability = this.availabiltyService.getScheduleDashboardSlot(Departments.SERVICE, startDate, startDate).subscribe(value => {
      if (value.data.availableDates && value.data.availableDates.length > 0) {
        this.servicePersonalSetup = value.data.availableDates[0].servicePersonalSetup;
      }
      if (this.servicePersonalSetup === 0) {
        this.settingsForm.get('capacity').get('calculationMethod').patchValue("DFRH");
      }
    })
    this.subs.shopMaxTotal = this.shopMaxTotal$?.subscribe(data => {
      if (data && data.length > 0) {
        const day: string = this.weekdays[new Date().getDay()];
        this.detailedCapcitySetup = data.find(x => x.dayOfWeek === day).slotCapacity ? data.find(x => x.dayOfWeek === day).slotCapacity : 0;
      }
      if (this.detailedCapcitySetup === 0) {
        this.settingsForm.get('capacity').get('slotsCalculationMethod').patchValue("DAS");
      }
    });
    this.curOffTime = this.settingsService.getCutOffTime();
    this.timeDuration = this.settingsService.getTimeDuration(this.department);
    this.subs.formChanges = this.settingsForm.valueChanges.subscribe(value => {
      if (value.capacity.tier1 < 0) {
        this.setMinError('tier1');
      }
      if (value.capacity.tier1 > 100) {
        this.setMaxError('tier1')
      }
      if (value.capacity.tier2 < 0) {
        this.setMinError('tier2');
      }
      if (value.capacity.tier2 > 100) {
        this.setMaxError('tier2')
      }
      if (value.capacity.tier3 < 0) {
        this.setMinError('tier3');
      }
      if (value.capacity.tier3 > 100) {
        this.setMaxError('tier3')
      }
      if (value.capacity.tier4 < 0) {
        this.setMinError('tier4');
      }
      if (value.capacity.tier4 > 100) {
        this.setMaxError('tier4')
      }
    })


  }

  canExit():Observable<boolean> { 
    const result = new Subject<boolean>();
    const dialogRef = this.dialog.open(ConfirmDialogComponent,exitConfig
      );
    this.subs.dialog = dialogRef.afterClosed().subscribe(event => {
      if (event.action === 'confirm') {
        this.saveChanges();
        result.next(true);
        result.complete();
      }
      else{
        result.next(true);
        result.complete();
      }
    });
    return result.asObservable(); 
  };

   // returns if the forms are edited
   formsNotEdited()
   {
     return !this.settingsForm.dirty; 
   }
  
  setUpStore() {
    const obj = {
      department: this.department,
    };
    this.settingsState.dispatch(new fromActions.LoadSettings(obj));
    this.slotState.dispatch(new fromSlotActions.LoadShopMaxTotal());
    this.settingsState.dispatch(new fromActions.LoadZipcodes(obj));
    this.settingsState.dispatch(new fromActions.LoadTimezones());
    this.settings$ = this.settingsState.select(fromSelector.selectSettings);
    this.shopMaxTotal$ = this.slotState.select(fromSlotSelector.selectShopMaxTotal);
    this.zipcodes$ = this.settingsState.select(fromSelector.selectZipcodes);
    this.timezones$ = this.settingsState.select(fromSelector.selectTimezones);


  }

  initializeSubscriptions() {
    this.subs.settingsLoaded = this.settingsState.select(fromSelector.selectSettingsLoaded).subscribe(loaded => {
      if (loaded) {
        this.saveInProgress = false;
      }
    });
    this.subs.settings = combineLatest(this.settings$, this.shopMaxTotal$).subscribe(([value, shopMax]) => {
      if (value) {
        this.hoursOfOperation = value.time.hoursOfOperation;
        this.amenities = this.amenities?.length === 0 ? value.portal?.amenities : this.amenities;
        this.hasAmenities = !!this.amenities?.length;
        const formData = JSON.parse(JSON.stringify(value));
        if (formData.portal) {
          if (!this.hasAmenities) {
            formData.portal.enableAmenities = false;
          } else if (!Object.keys(formData.portal).includes('enableAmenities')) {
            formData.portal.enableAmenities = true;
          }
        }
        if (value.capacity?.tiers) {
          const tiers = formData.capacity.tiers.reduce((obj, curr, index) => {
            const key = `tier${index + 1}`;
            return { ...obj, [key]: curr };
          }, {})
          delete formData.capacity.tiers;
          formData.capacity = { ...formData.capacity, ...tiers }
        }
        this.settingsForm.patchValue(formData);
        if (shopMax && shopMax.length > 0) {
          const day: string = this.weekdays[new Date().getDay()];
          this.detailedCapcitySetup = shopMax.find(x => x.dayOfWeek === day).slotCapacity;
        }
        if (this.detailedCapcitySetup === 0) {
          this.settingsForm.get('capacity').get('slotsCalculationMethod').patchValue("DAS");
        }
        if (!this.settingsForm.get('time').get('timeZone').value) {
          this.setupTimezoneValidation()
        }
        if (! +this.settingsForm.get('capacity').get('dailyAverageServiceSlots').value) {
          this.settingsForm.get('capacity').get('dailyAverageServiceSlots').markAsTouched();
          this.settingsForm.get('capacity').get('dailyAverageServiceSlots').updateValueAndValidity()
        }
        this.subs.zipcodes = this.zipcodes$.subscribe(data => {
          this.zipcodes = data;
        });
        this.initializeTransportOptions(value.transport.transportOptions);
        this.initializeNotification(value.notification);
        this.setSwitchValues(value);
      }
    })
  }

  initiateForm() {
    this.settingsForm = this.fb.group({
      transport: this.fb.group({
        transportOptions: this.fb.array([]),
        includedRemoteZipCodes: this.fb.control(''),
        estimatedDuration: this.fb.control('')
      }),
      notification: this.fb.group({
        syncFailure: this.fb.group({
          enable: [true],
          distributionList: this.fb.array([])
        }),
        rental: this.fb.group({
          enable: [false],
          distributionList: this.fb.array([])
        }),
        sscRecall: this.fb.group({
          enable: [false],
          distributionList: this.fb.array([])
        }),
        telematics: this.fb.group({
          enable: [false],
          distributionList: this.fb.array([])
        }),
        appointment: this.fb.group({
          enable: [false],
          distributionList: this.fb.array([])
        })
      }),
      time: this.fb.group({
        slotDuration: [''],
        cutOffTime: [''],
        garageEfficiency: ['', [Validators.required, Validators.min(0)]],
        aspFulfillmentPercent: ['', [Validators.required, Validators.min(0), Validators.max(100)]],
        timeZone: ['', Validators.required]
      }),
      portal: this.fb.group({
        showPricing: [false],
        mandateVin: [false],
        mandateCustomerAddress: [false],
        showEstimatedTime: [false],
        enableAmenities: [true],
        bannerTitleText: [''],
        bannerBodyText: [''],
        upsellText: ['']
      }),
      capacity: this.fb.group({
        dailyFRH: ['0', Validators.min(1)],
        calculationMethod: [''],
        tier1: [''],
        tier2: [''],
        tier3: [''],
        tier4: [''],
        dailyAverageServiceSlots: ['0', Validators.min(1)],
        slotsCalculationMethod: [''],
      }),
      alexaEnabled: [true],
      customerNotificationEnabled: [false],
      reminderNotificationEnabled: [false],
      reminderNotificationSchedule: ['']
    });
  }

  get dailyAverageFRH() {
    return this.settingsForm.get('capacity').get('dailyFRH').value;
  }

  get calculationMethod() {
    return this.settingsForm.get('capacity').get('calculationMethod').value;
  }

  get dailyAverageServiceSlots() {
    return this.settingsForm.get('capacity').get('dailyAverageServiceSlots').value;
  }

  get slotsCalculationMethod() {
    return this.settingsForm.get('capacity').get('slotsCalculationMethod').value;
  }

  get transportOptions() {
    return (this.settingsForm.controls.transport as UntypedFormGroup).controls.transportOptions as UntypedFormArray;
  }

  get estimatedDuration() {
    return (this.settingsForm.controls.transport as UntypedFormGroup).controls.estimatedDuration as UntypedFormControl;
  }
  get remoteTransport() {
    return TransportationType.REMOTE;
  }

  // Generated by Copilot- Start
  shouldHideTransportOption(optionId: string): boolean {
    const isRemoteTransportOption = optionId === TransportationType.REMOTE
      || optionId === TransportationType.DELIVERY
      || optionId === TransportationType.PICKUP;

    return isRemoteTransportOption && (!this.zipcodes || this.zipcodes.length === 0);
  }
  // Generated by Copilot- End


  onCapacityMethodChange(method) {
    this.settingsForm.get('capacity').get('calculationMethod').patchValue(method);
  }

  onSlotCapacityMethodChange(method) {
    this.settingsForm.get('capacity').get('slotsCalculationMethod').patchValue(method);
  }

  setZipcodeValidation(value, event) {

    const getValue = this.transportOptions.getRawValue();
    const transportCheckd = getValue.filter(item => item.isActive === true && item.category === 'At Remote');
   
    const zipcodesField = (this.settingsForm.get('transport.includedRemoteZipCodes') as UntypedFormControl);
    const estimatedDurationRemote = (this.settingsForm.get('transport.estimatedDuration') as UntypedFormControl);

    if (event.checked && (value.id === TransportationType.REMOTE || value.id === TransportationType.DELIVERY || value.id === TransportationType.PICKUP) && this.zipcodes && this.zipcodes.length > 0) {
      zipcodesField.setValidators([Validators.required]);
      zipcodesField.markAsTouched();
      zipcodesField.updateValueAndValidity();
      zipcodesField.enable();
      estimatedDurationRemote.setValidators([Validators.required]);
      estimatedDurationRemote.markAsTouched();
      estimatedDurationRemote.updateValueAndValidity();
      estimatedDurationRemote.enable();
    }
    else if (event.checked === false && transportCheckd?.length === 0) {
      zipcodesField.clearValidators();
      zipcodesField.updateValueAndValidity();
      zipcodesField.disable();
      estimatedDurationRemote.clearValidators();
      estimatedDurationRemote.updateValueAndValidity();
      estimatedDurationRemote.disable();
    }
    else {
      estimatedDurationRemote.clearValidators();
      estimatedDurationRemote.updateValueAndValidity();
      zipcodesField.clearValidators();
      zipcodesField.updateValueAndValidity();
    }
  }

  setReminderNotifValidation() {
    const reminderNotifField = (this.settingsForm.get('reminderNotificationEnabled') as UntypedFormControl);
    if (this.settingsForm.get('customerNotificationEnabled').value) {
      reminderNotifField.markAsTouched();
      reminderNotifField.updateValueAndValidity();
      reminderNotifField.enable();
    } else {
      reminderNotifField.clearValidators();
      reminderNotifField.updateValueAndValidity();
      reminderNotifField.disable();
    }
    this.setReminderScheduleValidation();
  }

  setReminderScheduleValidation() {
    const reminderScheduleField = (this.settingsForm.get('reminderNotificationSchedule') as UntypedFormControl);
    if (this.settingsForm.get('reminderNotificationEnabled').value && this.settingsForm.get('customerNotificationEnabled').value) {
      reminderScheduleField.setValidators([Validators.required]);
      reminderScheduleField.markAsTouched();
      reminderScheduleField.updateValueAndValidity();
      reminderScheduleField.enable();
    } else {
      reminderScheduleField.clearValidators();
      reminderScheduleField.updateValueAndValidity();
      reminderScheduleField.disable();
    }
  }

  setupTimezoneValidation() {
    this.settingsForm.get('time').get('timeZone').markAsTouched();
    this.settingsForm.get('time').get('timeZone').updateValueAndValidity();
  }

  onChangeValue(checked, index) {
    const transportOptions = 'transport.transportOptions';
    if (!checked) {
      this.settingsForm.get(transportOptions)['at'](index).get('cost').disable();
      this.settingsForm.get(transportOptions)['at'](index).get('flatRateHours').disable();
      this.settingsForm.get(transportOptions)['at'](index).get('estimatedDuration').disable();
      this.settingsForm.get(transportOptions)['at'](index).get('comments').disable();
      this.settingsForm.get(transportOptions)['at'](index).get('distance').disable();
    }
    else {
      this.settingsForm.get(transportOptions)['at'](index).get('cost').enable();
      this.settingsForm.get(transportOptions)['at'](index).get('flatRateHours').enable();
      this.settingsForm.get(transportOptions)['at'](index).get('comments').enable();
      this.settingsForm.get(transportOptions)['at'](index).get('estimatedDuration').enable();
      this.settingsForm.get(transportOptions)['at'](index).get('distance').enable();
    }

  }

  initializeTransportOptions(transport) {
    this.transportOptions.clear();
    let time = null;
    transport.forEach((trans, index) => {
      time = (trans.id === 'REMOTE' || trans.id === 'PICKUP' || trans.id === 'DELIVERY') ? trans?.estimatedDuration : time;
      this.transportOptions.push(new UntypedFormGroup({
        id: new UntypedFormControl(trans.id),
        category: new UntypedFormControl(trans.category),
        label: new UntypedFormControl(trans.label),
        isActive: new UntypedFormControl(trans.isActive),
        comments: new UntypedFormControl(trans.comments),
        cost: new UntypedFormControl(trans.cost),
        flatRateHours: new UntypedFormControl(trans.flatRateHours),
        distance: new UntypedFormControl(trans.distance),
        estimatedDuration: new UntypedFormControl(trans.estimatedDuration),

      }));
      this.estimatedDuration.patchValue(time);

      if (trans.id === (TransportationType.REMOTE || TransportationType.PICKUP || TransportationType.DELIVERY) && this.zipcodes && this.zipcodes.length > 0) {
        this.setZipcodeValidation(trans, { checked: trans.isActive });
      }
      this.onChangeValue(trans.isActive, index)
    })
  }

  initializeNotification(notification) {
    this.notificationList.forEach(type => {
      this.distributionList(type.name).clear();
      if (notification[type.name] && notification[type.name].distributionList) {
        notification[type.name].distributionList.forEach(email => {
          this.addDistributionList(type.name, email);
        });
      }
    })
  }

  saveChanges() {
    if (this.settingsForm.valid) {
      const formData = this.settingsForm.getRawValue();
      //const duration = formData.transport.transportOptions.find(item=> item.id === 'DELIVERY');
      formData.transport.transportOptions.forEach((element, index) => {
        if (element.id === 'REMOTE' || element.id === 'PICKUP' || element.id === 'DELIVERY') {
          formData.transport.transportOptions[index].estimatedDuration = formData.transport.estimatedDuration;
        }
      });
      formData.time.hoursOfOperation = this.hoursOfOperation;
      formData.capacity = {
        calculationMethod: formData.capacity.calculationMethod,
        dailyFRH: formData.capacity.dailyFRH,
        tiers: [formData.capacity.tier1 ? formData.capacity.tier1 : 100, formData.capacity.tier2 ? formData.capacity.tier2 : 100, formData.capacity.tier3 ? formData.capacity.tier3 : 100, formData.capacity.tier4 ? formData.capacity.tier4 : 100],
        dailyAverageServiceSlots: formData.capacity.dailyAverageServiceSlots,
        slotsCalculationMethod: formData.capacity.slotsCalculationMethod
      }
      const obj = {
        department: this.department,
        settings: formData
      };
      this.saveInProgress = true;
      this.settingsState.dispatch(new fromActions.UpdateSettings(obj));
    } else {
      this.alertService.showAlert('otherTexts.pleaseUpdateInvalidFields');
    }
    this.settingsForm.markAsPristine();
  }

  cancel() {
    this.settingsForm.reset();
    this.initializeSubscriptions();
  }

  setSwitchValues(obj) {
    this.portalSwitches.showPricing = obj.portal.showPricing;
    this.portalSwitches.showEstimatedTime = obj.portal.showEstimatedTime;
    this.portalSwitches.mandateVin = obj.portal.mandateVin;
    this.portalSwitches.mandateCustomerAddress = obj.portal.mandateCustomerAddress;
    if (!this.hasAmenities) {
      this.portalSwitches.enableAmenities = false;
    } else if (!Object.keys(obj.portal).includes('enableAmenities')) {
      this.portalSwitches.enableAmenities = true;
    } else {
      this.portalSwitches.enableAmenities = obj.portal.enableAmenities;
    }
    // this.notification.syncFailure = obj.notification.syncFailure.enable;
    this.notification.rental = obj.notification.rental?.enable;
    this.notification.sscRecall = obj.notification.sscRecall.enable;
    this.notification.telematics = obj.notification.telematics.enable;
    this.notification.appointment = obj.notification.appointment.enable;
    this.alexaEnabled = obj.alexaEnabled;
    this.customerNotificationEnabled = obj.customerNotificationEnabled;
    this.reminderNotificationEnabled = obj.reminderNotificationEnabled;
    this.setReminderNotifValidation();
    if (obj.dealerCode && obj.dealerCode.startsWith('6')) {
      this.dealerLexusFlag = true;
    }
  }

  updateSwitchValues(val: boolean, property: string) {
    switch (property) {
      case 'showPricing':
        this.settingsForm.patchValue({
          portal: {
            showPricing: val
          }
        });
        break;

      case 'showEstimatedTime':
        this.settingsForm.patchValue({
          portal: {
            showEstimatedTime: val
          }
        });
        break;

      case 'mandateVin':
        this.settingsForm.patchValue({
          portal: {
            mandateVin: val
          }
        });
        break;

      case 'mandateCustomerAddress':
        this.settingsForm.patchValue({
          portal: {
            mandateCustomerAddress: val
          }
        });
        break;

      case 'enableAmenities':
        this.settingsForm.patchValue({
          portal: {
            enableAmenities: val
          }
        });
        break;


      case 'syncFailure':
        this.settingsForm.patchValue({
          notification: {
            syncFailure: {
              enable: val
            }
          }
        });
        break;

      case 'rental':
        this.settingsForm.patchValue({
          notification: {
            rental: {
              enable: val
            }
          }
        });
        break;

      case 'sscRecall':
        this.settingsForm.patchValue({
          notification: {
            sscRecall: {
              enable: val
            }
          }
        });
        break;

      case 'telematics':
        this.settingsForm.patchValue({
          notification: {
            telematics: {
              enable: val
            }
          }
        });
        break;

      case 'appointment':
        this.settingsForm.patchValue({
          notification: {
            appointment: {
              enable: val
            }
          }
        });
        break;

      case 'alexaEnabled':
        this.settingsForm.get('alexaEnabled').patchValue(val);
        break;

      case 'customerNotificationEnabled':
        this.settingsForm.get('customerNotificationEnabled').patchValue(val);
        if (!val) {
          this.settingsForm.get('reminderNotificationEnabled').patchValue(val);
          this.reminderNotificationEnabled = false;
        }
        this.setReminderNotifValidation();
        break;

      case 'reminderNotificationEnabled':
        this.settingsForm.get('reminderNotificationEnabled').patchValue(val);
        this.reminderNotificationEnabled = val
        this.setReminderScheduleValidation();
        break;

      default:
        break;
    }
    this.settingsForm.markAsDirty();
  }


  setAlertConHight(portalcon) {
    const portalConHeight = 621;
    const alertConHeight = 600;
    return portalcon.clientHeight > portalConHeight ? (alertConHeight + (portalcon.clientHeight - portalConHeight)) : alertConHeight
  }

  distributionList(type) {
    return this.settingsForm.get('notification').get(type).get('distributionList') as UntypedFormArray;
  }

  isEnable(type) {
    return this.settingsForm.get('notification').get(type).get('enable').value;
  }

  addDistributionList(type, value = '') {
    const checklist = this.fb.control(value, [Validators.required, Validators.email, this.noWhitespaceValidator]);
    this.distributionList(type).push(checklist);
  }

  removeDistributionList(type, i) {
    this.distributionList(type).removeAt(i);
  }

  public noWhitespaceValidator(control: UntypedFormControl) {
    const isWhitespace = (control.value || '').trim().length === 0;
    const isValid = !isWhitespace;
    return isValid ? null : { 'whitespace': true };
  }
  setMinError(control: string) {
    this.settingsForm.controls['capacity'].get(control).setErrors({ required: this.translate.instant('generalSettings.greaterThan0') })
  }
  setMaxError(control: string) {
    this.settingsForm.controls['capacity'].get(control).setErrors({ required: this.translate.instant('generalSettings.lesserThan100') })
  }
  getTranslation(value) {
    if (value === 'Remote') {
      value = 'RemoteService';
     return  this.translate.instant(`common.${value}`)
    }
    else {
      if(value === "Rideshare" && this.translate.currentLang.split("-")[1] === "MX"){
        return this.translate.instant(`service.complimentaryTransportation`)
      }
      return this.translate.instant(`common.${value}`)
    }
    
  }

  navigateToDIS() {
    window.open(environment.dis, "_blank");
  }

  getIntegrationText(){
    return this.translate.instant('otherTexts.syncFromDis');
  }

  syncDealerDisDetails() {
    this.runSyncWithRetry(1);
  }

  showError(){
    this.syncInProgress = false;
    this.alertService.showAlert('otherTexts.somethingWentWrongPleaseTryAgain');
  }

  private runSyncWithRetry(attempt: number) {
    this.syncInProgress = true;
    this.settingsService.syncDealerDisDetails().subscribe(res => {
      if(res.message === 'Index Updated Successfully'){
        this.syncInProgress = false;
        this.setUpStore();
        this.initializeSubscriptions();
        this.alertService.showAlert('otherTexts.syncedSuccessfully', '', '', 5000, false);
        return;
      }

      this.handleSyncFailure(attempt);
    }, _error => {
      this.handleSyncFailure(attempt);
    });
  }

  private handleSyncFailure(attempt: number) {
    this.syncInProgress = false;
    if (attempt >= 3) {
      this.alertService.showEscalationAlert();
      return;
    }

    this.subs.syncRetry = this.alertService.openRetryPopup(this.translate.instant('otherTexts.somethingWentWrongPleaseTryAgain'))
      .subscribe((shouldRetry) => {
        if (shouldRetry) {
          this.runSyncWithRetry(attempt + 1);
        }
      });
  }

  ngOnDestroy() {
    util.unsubscribeSubscriptions(this.subs);
  }
}
